import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Leaf, Droplets, Sparkles, Flame, Coffee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Product type configuration
const PRODUCT_TYPES = [
  { id: 'all', label: 'All Products', icon: Sparkles },
  { id: 'teas', label: 'Herbal Teas', icon: Leaf },
  { id: 'oils', label: 'Essential Oils', icon: Droplets },
  { id: 'bath', label: 'Bath & Body', icon: Sparkles },
  { id: 'candles', label: 'Candles', icon: Flame },
  { id: 'accessories', label: 'Tea Accessories', icon: Coffee },
];

// Bath & body subtypes
const BATH_TYPES = ['bar_soap', 'bath_bomb', 'body_cream', 'sugar_scrub', 'whipped_butter', 'whipped_soap'];

export default function ShopPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let data = [];
      let cats = [];

      if (selectedType === 'all' || selectedType === 'teas') {
        const teasRes = await axios.get(`${API}/teas`);
        if (selectedType === 'all' || selectedType === 'teas') {
          data = [...data, ...teasRes.data.map(t => ({ ...t, productType: 'tea' }))];
        }
        // Get tea categories
        const uniqueCats = [...new Set(teasRes.data.map(t => t.category))];
        cats = [...cats, ...uniqueCats];
      }

      if (selectedType === 'all' || selectedType === 'oils') {
        const oilsRes = await axios.get(`${API}/collections/oils`);
        data = [...data, ...oilsRes.data.map(p => ({ ...p, productType: 'oil' }))];
      }

      if (selectedType === 'all' || selectedType === 'bath') {
        const bathRes = await axios.get(`${API}/collections/bath-body`);
        data = [...data, ...bathRes.data.map(p => ({ ...p, productType: 'bath' }))];
        // Add bath subtypes as categories
        if (selectedType === 'bath') {
          cats = ['Bar Soaps', 'Bath Bombs', 'Body Creams', 'Sugar Scrubs', 'Whipped Butters', 'Whipped Soaps'];
        }
      }

      if (selectedType === 'all' || selectedType === 'candles') {
        const candlesRes = await axios.get(`${API}/collections/candles`);
        data = [...data, ...candlesRes.data.map(p => ({ ...p, productType: 'candle' }))];
        // Add candle categories
        if (selectedType === 'candles') {
          const candleCats = [...new Set(candlesRes.data.map(c => c.category).filter(Boolean))];
          cats = candleCats.length > 0 ? candleCats : ['Relaxation', 'Energizing', 'Seasonal'];
        }
      }

      if (selectedType === 'all' || selectedType === 'accessories') {
        const accRes = await axios.get(`${API}/collections/accessories`);
        data = [...data, ...accRes.data.map(p => ({ ...p, productType: 'accessory' }))];
      }

      setItems(data);
      setCategories([...new Set(cats)]);
      setSelectedCategory('all');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
      item.category === selectedCategory ||
      (selectedType === 'bath' && getCategoryLabel(item.type) === selectedCategory);
    
    return matchesSearch && matchesCategory;
  });

  // Helper to get category label for bath products
  const getCategoryLabel = (type) => {
    const labels = {
      'bar_soap': 'Bar Soaps',
      'bath_bomb': 'Bath Bombs',
      'body_cream': 'Body Creams',
      'sugar_scrub': 'Sugar Scrubs',
      'whipped_butter': 'Whipped Butters',
      'whipped_soap': 'Whipped Soaps'
    };
    return labels[type] || type;
  };

  // Navigate to product detail
  const handleProductClick = (item) => {
    if (item.productType === 'tea') {
      navigate(`/tea/${item.id}`);
    } else {
      navigate(`/product/${item.id}`);
    }
  };

  // Get badge color based on product type
  const getBadgeColor = (productType) => {
    const colors = {
      tea: 'bg-emerald-100 text-emerald-700',
      oil: 'bg-purple-100 text-purple-700',
      bath: 'bg-pink-100 text-pink-700',
      candle: 'bg-amber-100 text-amber-700',
      accessory: 'bg-blue-100 text-blue-700'
    };
    return colors[productType] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.88), rgba(204, 85, 0, 0.88)), url(https://images.unsplash.com/photo-1504382103100-db7e92322d39?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="shop-title">
          Shop Our Collection
        </h1>
        <p className="text-white/80 mb-8 text-lg">
          Discover {items.length}+ all-natural products for your wellness journey
        </p>

        {/* Product Type Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {PRODUCT_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setSelectedType(type.id)}
                data-testid={`filter-${type.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  selectedType === type.id
                    ? 'bg-white text-orange-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            );
          })}
        </div>

        {/* Search and Category Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search products..."
              className="pl-10 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
          {categories.length > 0 && (
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-64 bg-white" data-testid="category-select">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Results count */}
        <p className="text-white/70 mb-4">
          Showing {filteredItems.length} {filteredItems.length === 1 ? 'product' : 'products'}
        </p>

        {/* Loading state */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : (
          /* Product Grid */
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems.map(item => (
              <div
                key={`${item.productType}-${item.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => handleProductClick(item)}
                data-testid={`product-card-${item.id}`}
              >
                <div className="relative">
                  <img src={item.image_url} alt={item.name} className="w-full h-48 object-cover" />
                  <span className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full ${getBadgeColor(item.productType)}`}>
                    {item.productType === 'tea' ? 'Tea' : 
                     item.productType === 'oil' ? 'Oil' : 
                     item.productType === 'bath' ? 'Bath & Body' : 
                     item.productType === 'candle' ? 'Candle' : 'Accessory'}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-orange-600">${item.price.toFixed(2)}</span>
                    {item.category && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{item.category}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white text-xl">No products found matching your criteria.</p>
            <button 
              onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
              className="mt-4 text-white/80 underline hover:text-white"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
